import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

/**
 * The records related to an owner, one list per tab.
 *
 * A plain dense table rather than a data grid: these lists are short, live inside a dialog,
 * and need no sorting or column machinery of their own — the full-featured grid is one
 * click away on the page the rows link to.
 *
 * @param columns [{field, headerName, width, render}] - render is optional
 * @param rows the records to show
 * @param onOpen called with a row when it is clicked; omit to make rows inert
 */
const RelatedTable = ({columns, rows, loading, emptyText, onOpen, maxHeight}) => {
  if (loading) return <LinearProgress sx={{my: 2}}/>;

  if (!rows.length) {
    return (
      <Typography variant='body2' color='textSecondary' sx={{py: 3, textAlign: 'center'}}>
        {emptyText}
      </Typography>
    );
  }

  return (
    // Capped and scrolled so a farm with fifty streams cannot push the dialog's buttons off
    // the screen, and so cards sitting side by side stay the same height.
    <Box sx={{maxHeight, overflowY: 'auto'}}>
      <Table size='small' stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.field} sx={{width: column.width, fontWeight: 600}}>
                {column.headerName}
              </TableCell>
            ))}
            {onOpen && <TableCell sx={{width: 48}}/>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.id}
              hover={Boolean(onOpen)}
              onClick={onOpen ? () => onOpen(row) : undefined}
              sx={onOpen ? {cursor: 'pointer'} : undefined}
            >
              {columns.map((column) => (
                <TableCell key={column.field}>
                  {column.render ? column.render(row) : row[column.field]}
                </TableCell>
              ))}
              {onOpen && (
                <TableCell padding='none' align='right'>
                  <OpenInNewIcon fontSize='small' color='action'/>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

RelatedTable.propTypes = {
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array,
  loading: PropTypes.bool,
  emptyText: PropTypes.string,
  onOpen: PropTypes.func,
  maxHeight: PropTypes.number,
};

RelatedTable.defaultProps = {
  rows: [],
  loading: false,
  emptyText: 'Nothing to show.',
  onOpen: undefined,
  maxHeight: 232,
};

export default RelatedTable;
